'use client';

import {
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import { Button } from '@/app/Components/Button';

export default function ScheduleGettingStarted({
  termLabel,
  onOpenPlanner,
}: {
  termLabel: string;
  onOpenPlanner: () => void;
}) {
  const steps = [
    {
      icon: MagnifyingGlassIcon,
      title: 'Add your courses',
      detail: 'Search by name or Rutgers course code.',
    },
    {
      icon: AdjustmentsHorizontalIcon,
      title: 'Set what matters',
      detail: 'Choose availability, campuses, and priorities.',
    },
    {
      icon: SparklesIcon,
      title: 'Compare options',
      detail: 'Generate schedules and keep the best fit.',
    },
  ];

  return (
    <section
      aria-labelledby="schedule-getting-started-title"
      className="flex min-h-[30rem] items-center justify-center px-4 py-8 sm:px-8"
    >
      <div className="w-full max-w-3xl text-center">
        <span className="inline-flex rounded-control bg-[var(--ss-brand-bg)] px-2.5 py-1 text-xs font-semibold text-[var(--ss-focus)]">
          Your {termLabel} schedule
        </span>
        <h3 id="schedule-getting-started-title" className="mt-4 text-2xl font-bold text-[var(--ss-text)] sm:text-3xl">
          Start with the courses you might take
        </h3>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[var(--ss-text-muted)] sm:text-base">
          Add a few courses first. Scarlet Sync will combine their sections with your availability and preferences to build schedules you can compare.
        </p>

        <ol className="mt-7 grid gap-3 text-left sm:grid-cols-3">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-[var(--ss-surface-subtle)] text-[var(--ss-focus)]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">Step {index + 1}</span>
                </div>
                <p className="mt-3 font-semibold text-[var(--ss-text)]">{step.title}</p>
                <p className="mt-1 text-sm leading-5 text-[var(--ss-text-muted)]">{step.detail}</p>
              </li>
            );
          })}
        </ol>

        <Button onClick={onOpenPlanner} className="mt-7 min-w-52">
          Open Course Planner
        </Button>
        <p className="mt-3 text-xs text-[var(--ss-text-muted)]">You can change every choice later.</p>
      </div>
    </section>
  );
}
